import { IconButton } from "@mui/material"
import { VisibilityOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import { useCallback, useMemo } from "react"
import type { ReactElement } from "react"

import { findCompanyType } from "../../utils/company"

import Chip from "@/components/common/Chip/Chip"
import LimitedSizeChipGroup from "@/components/common/Chip/LimitedSizeChipGroup"
import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import type { Company, CustomerAccountStatus } from "@/graphql/codegen/graphql"
import { CompanySortField, CompanyStatus } from "@/graphql/codegen/graphql"
import { AccountManagersCell } from "@/screens/Companies/components/tables/companies.accountManager"
import { CUSTOMER_ACCOUNT_STATUS_LABEL_MAP } from "@/screens/Companies/constants/customerAccountDataMap"
import { useCompanyTypesStore } from "@/screens/Companies/stores/useCompanyTypesStore"
import { createStatusMap } from "@/utils/statusMap"

export const companyStatusMap = createStatusMap<
  CompanyStatus,
  "primary" | "secondary" | "default" | "error" | "info" | "success" | "warning"
>([
  [CompanyStatus.Active, "success"],
  [CompanyStatus.Pending, "warning"],
  [CompanyStatus.Rejected, "error"],
  [CompanyStatus.Inactive, "secondary"],
])

export type SortableAccessorKey = "accountManagers" | "types" | "name"

export const accessorKeyToCompanySortField: ReadonlyMap<SortableAccessorKey, CompanySortField> = new Map([
  ["accountManagers", CompanySortField.AccountManager],
  ["types", CompanySortField.CompanyType],
  ["name", CompanySortField.Name],
])

export const companySortFieldToAccessoryKey: ReadonlyMap<CompanySortField, SortableAccessorKey> = new Map([
  [CompanySortField.AccountManager, "accountManagers"],
  [CompanySortField.CompanyType, "types"],
  [CompanySortField.Name, "name"],
])

const CompanyTypeCell = ({ types, maxSize }: { types: string[]; maxSize: number }): ReactElement => {
  const companyTypes = useCompanyTypesStore((state) => state.companyTypes)
  const getChipName = useCallback((type: string) => findCompanyType(companyTypes, type)?.name, [companyTypes])

  const chips = useMemo(() => types.map((type, index) => ({ id: `${type}-${index}`, name: type })), [types])

  return (
    <LimitedSizeChipGroup
      chips={chips}
      maxSize={maxSize}
      renderChip={(chip) => {
        const label = getChipName(chip.name)
        return (
          <Chip
            className='mr-2 rounded'
            key={chip.id}
            label={label}
            icon={chip?.icon}
            color='primary'
            style={{ maxWidth: chip?.maxWidth ?? "auto" }}
          />
        )
      }}
    />
  )
}

export const useCompanyColumns = (): ColumnDef<Company>[] => {
  const navigate = useNavigate()
  const handleCompanyRowClick = (companyId: string) =>
    void navigate({ from: "/app/companies", to: `${companyId}/edit` })

  return useMemo(
    () => [
      {
        accessorKey: "status",
        header: "",
        cell: ({ getValue, cell }: CellContext<Company, unknown>): ReactElement => (
          <StatusIndicator
            data-testid={`${cell.id}-${getValue()}`}
            status={getValue() as string}
            map={companyStatusMap}
          />
        ),
        size: 24,
        enableSorting: false,
      },
      {
        accessorKey: "name",
        header: "Company Name",
        sortingFn: "alphanumeric",
        enableSorting: true,
        size: 400,
      },
      {
        accessorKey: "types",
        header: "Company Type",
        cell: ({ getValue, cell }: CellContext<Company, unknown>): ReactElement => (
          <CompanyTypeCell types={getValue() as string[]} maxSize={cell.column.getSize()} />
        ),
        enableSorting: true,
        size: 400,
      },
      {
        accessorKey: "customerAccountStatus",
        header: "Customer Account Status",
        cell: ({ row }) => {
          const customerAccountStatus = row.original.customerAccountStatus

          return customerAccountStatus
            ? CUSTOMER_ACCOUNT_STATUS_LABEL_MAP[customerAccountStatus as CustomerAccountStatus]
            : ""
        },

        enableSorting: false,
      },
      {
        accessorKey: "accountManagers",
        header: "Account Managers",
        cell: ({ row }: CellContext<Company, unknown>): ReactElement => (
          <AccountManagersCell accountManagers={row.original.accountManagers} />
        ),
        sortingFn: "alphanumeric",
        enableSorting: true,
      },
      {
        accessorKey: "actions",
        header: "",
        cell: ({ row }) => (
          <IconButton
            onClick={() => handleCompanyRowClick(row.original.companyId)}
            onKeyUp={(e) => e.key === "Enter" && handleCompanyRowClick(row.original.companyId)}
          >
            <VisibilityOutlined className='block text-gray-600' />
          </IconButton>
        ),
        size: 100,
        enableSorting: false,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
}
